import { Extension } from "@tiptap/core";
import CharacterCount from "@tiptap/extension-character-count";
import Highlight from "@tiptap/extension-highlight";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import TaskItem from "@tiptap/extension-task-item";
import TaskList from "@tiptap/extension-task-list";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Plugin, PluginKey } from "prosemirror-state";
import { useEffect, useRef } from "react";
import { MenuBar, blobToBase64 } from "./MenuBar";
import "./tiptapEditor.css";

const CustomEventHandlers = Extension.create({
	name: "CustomEventHandlers",

	addProseMirrorPlugins() {
		return [
			new Plugin({
				key: new PluginKey("customEventHandlers"),
				props: {
					handleDOMEvents: {
						paste: async (_, event) => {
							const file = event.clipboardData.files?.[0];
							if (file && file.type.startsWith("image/")) {
								event.preventDefault();
								const src = await blobToBase64(file);
								src && this.editor.chain().focus().setImage({ src }).run();
							}
						},
						drop: async (_, event) => {
							const file = event.dataTransfer.files?.[0];
							if (file && file.type.startsWith("image/")) {
								event.preventDefault();
								const src = await blobToBase64(file);
								src && this.editor.chain().focus().setImage({ src }).run();
							}
						},
					},
				},
			}),
		];
	},
});

export const TipTap = (props = {}) => {
	const { value = "", onChange = () => {} } = props;

	const hasInitialized = useRef(false);
	const lastExternalValue = useRef(value);

	const editor = useEditor({
		extensions: [
			StarterKit.configure({
				heading: { levels: [1, 2, 3] },
				history: { depth: 100 },
			}),
			Underline,
			Highlight.configure({ multicolor: false }),
			Link.configure({
				openOnClick: false,
				HTMLAttributes: {
					rel: "noopener noreferrer",
					target: "_blank",
				},
			}),
			TaskList,
			TaskItem.configure({ nested: true }),
			Image.configure({ inline: false, allowBase64: true }),
			CustomEventHandlers,
			Placeholder.configure({
				placeholder:
					"مقاله را اینجا بنویسید… از تیتر، لیست، نقل‌قول و تصویر استفاده کنید.",
			}),
			CharacterCount.configure({ limit: 100000 }),
			TextAlign.configure({
				types: ["heading", "paragraph"],
				alignments: ["left", "right", "center", "justify"],
				defaultAlignment: "right",
			}),
		],
		content: value || "",
		immediatelyRender: false,
		editorProps: {
			attributes: {
				dir: "rtl",
				class: "publication-prose",
			},
		},
		onUpdate({ editor: ed }) {
			if (!hasInitialized.current) {
				hasInitialized.current = true;
				return;
			}
			const content = ed.getHTML();
			lastExternalValue.current = content;
			if (typeof onChange === "function") {
				onChange(content);
			}
		},
	});

	useEffect(() => {
		if (!editor) return;
		if (value === lastExternalValue.current) return;
		if (editor.isFocused) return;
		hasInitialized.current = false;
		editor.commands.setContent(value || "", false);
		lastExternalValue.current = value || "";
	}, [editor, value]);

	const charCount = editor?.storage.characterCount?.characters?.() ?? 0;
	const wordCount = editor?.storage.characterCount?.words?.() ?? 0;

	return (
		<div className="publication-editor flex h-[min(560px,70vh)] w-full flex-col overflow-hidden rounded-[14px] border border-darkBlue/15 bg-white shadow-[0_4px_20px_rgba(39,106,166,0.06)]">
			{editor && <MenuBar editor={editor} />}
			<EditorContent
				className="min-h-0 flex-1 overflow-y-auto px-4 py-4 md:px-6 md:py-5"
				style={{ WebkitOverflowScrolling: "touch" }}
				editor={editor}
			/>
			{editor && (
				<div className="flex flex-wrap items-center justify-between gap-2 border-t border-darkBlue/10 bg-[#F8FAFD] px-4 py-2 font-Vazir-400 text-[12px] text-darkBlue/50">
					<span>حداکثر ۱۰۰٬۰۰۰ نویسه</span>
					<span>
						{charCount.toLocaleString("fa-IR")} نویسه
						<span className="mx-1.5 text-darkBlue/25">|</span>
						{wordCount.toLocaleString("fa-IR")} کلمه
					</span>
				</div>
			)}
		</div>
	);
};
